import type { DevicePresence, RemoteDevice } from '../model.js';
import type {
  DeviceColor,
  DeviceCustomization,
  DeviceIcon,
  DirectorySort,
} from './customization.js';
import type { IconName } from '../components/icons.js';

/** A device as the directory shows it: the adapter's facts plus the person's labels. */
export interface DirectoryDevice extends RemoteDevice {
  readonly icon: DeviceIcon;
  readonly color: DeviceColor;
  readonly reportedName: string;
  readonly reportedGroup: string;
  readonly customized: boolean;
}

/** Which stroke icon draws each device icon choice. */
export const deviceIconGlyph: Readonly<Record<DeviceIcon, IconName>> = {
  monitor: 'devices',
  tower: 'tower',
  laptop: 'laptop',
  server: 'server',
  nas: 'nas',
  mini: 'mini',
  gpu: 'gpu',
  tv: 'tv',
  tablet: 'tablet',
  phone: 'phone',
  cloud: 'cloud',
  gamepad: 'gamepad',
  router: 'router',
};

export function defaultIcon(platform: RemoteDevice['platform']): DeviceIcon {
  return platform === 'linux' ? 'server' : platform === 'macos' ? 'laptop' : 'monitor';
}

export function personalize(
  device: RemoteDevice,
  customization: DeviceCustomization | undefined,
): DirectoryDevice {
  return {
    ...device,
    name: customization?.name || device.name,
    group: customization?.group || device.group,
    icon: customization?.icon ?? defaultIcon(device.platform),
    color: customization?.color ?? 'mint',
    reportedName: device.name,
    reportedGroup: device.group,
    customized: customization !== undefined,
  };
}

export function matchesQuery(device: DirectoryDevice, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [device.name, device.reportedName, device.hostname, device.group, device.osVersion]
    .join(' ')
    .toLowerCase()
    .includes(needle);
}

const presenceRank: Readonly<Record<DevicePresence, number>> = {
  online: 0,
  unchecked: 1,
  offline: 2,
  revoked: 3,
};
const byName = (a: DirectoryDevice, b: DirectoryDevice) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true });

export function sortDevices(
  devices: readonly DirectoryDevice[],
  sort: DirectorySort,
): DirectoryDevice[] {
  const sorted = [...devices];
  switch (sort) {
    case 'status':
      return sorted.sort(
        (a, b) => presenceRank[a.presence] - presenceRank[b.presence] || byName(a, b),
      );
    case 'seen':
      return sorted.sort((a, b) => {
        const left = a.lastSeenAt ? Date.parse(a.lastSeenAt) : Number.NEGATIVE_INFINITY;
        const right = b.lastSeenAt ? Date.parse(b.lastSeenAt) : Number.NEGATIVE_INFINITY;
        return right - left || byName(a, b);
      });
    default:
      return sorted.sort(byName);
  }
}

export interface DeviceGroup {
  readonly name: string;
  readonly devices: readonly DirectoryDevice[];
}

/** Groups in alphabetical order; devices keep the order they were given. */
export function groupDevices(devices: readonly DirectoryDevice[]): DeviceGroup[] {
  const groups = new Map<string, DirectoryDevice[]>();
  for (const device of devices) {
    const list = groups.get(device.group);
    if (list) list.push(device);
    else groups.set(device.group, [device]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }))
    .map(([name, list]) => ({ name, devices: list }));
}
